import { useState, useRef, useEffect } from "react";
import { HiX, HiZoomIn, HiZoomOut, HiRefresh } from "react-icons/hi";

const ImageViewer = ({ src, alt, isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const constrainPosition = (newPosition, newScale) => {
    if (!containerRef.current || !imageRef.current) return newPosition;
    
    const container = containerRef.current.getBoundingClientRect();
    const imageNaturalWidth = imageRef.current.naturalWidth;
    const imageNaturalHeight = imageRef.current.naturalHeight;
    
    const scaledWidth = imageNaturalWidth * newScale;
    const scaledHeight = imageNaturalHeight * newScale;
    
    const maxX = Math.max(0, (scaledWidth - container.width) / 2);
    const maxY = Math.max(0, (scaledHeight - container.height) / 2);
    
    return {
      x: Math.max(-maxX, Math.min(maxX, newPosition.x)),
      y: Math.max(-maxY, Math.min(maxY, newPosition.y))
    };
  };

  const handleZoomIn = () => {
    const newScale = Math.min(scale + 0.5, 3);
    const constrainedPosition = constrainPosition(position, newScale);
    setScale(newScale);
    setPosition(constrainedPosition);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - 0.5, 0.5);
    const constrainedPosition = constrainPosition(position, newScale);
    setScale(newScale);
    setPosition(constrainedPosition);
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse events
  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      const newPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      };
      setPosition(constrainPosition(newPosition, scale));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && isDragging && scale > 1) {
      const touch = e.touches[0];
      const newPosition = {
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      };
      setPosition(constrainPosition(newPosition, scale));
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const newScale = e.deltaY < 0 
      ? Math.min(scale + 0.2, 3) 
      : Math.max(scale - 0.2, 0.5);
    
    const constrainedPosition = constrainPosition(position, newScale);
    setScale(newScale);
    setPosition(constrainedPosition);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      {/* Controls */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button
          onClick={handleZoomOut}
          className="p-2 bg-[#112240] text-cyan-400 rounded-lg hover:bg-cyan-600 hover:text-white transition-all duration-300"
        >
          <HiZoomOut className="text-xl" />
        </button>
        <button
          onClick={handleZoomIn}
          className="p-2 bg-[#112240] text-cyan-400 rounded-lg hover:bg-cyan-600 hover:text-white transition-all duration-300"
        >
          <HiZoomIn className="text-xl" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-[#112240] text-cyan-400 rounded-lg hover:bg-cyan-600 hover:text-white transition-all duration-300"
        >
          <HiRefresh className="text-xl" />
        </button>
        <button
          onClick={onClose}
          className="p-2 bg-[#112240] text-cyan-400 rounded-lg hover:bg-red-600 hover:text-white transition-all duration-300"
        >
          <HiX className="text-xl" />
        </button>
      </div>

      {/* Zoom indicator */}
      <div className="absolute top-4 left-4 bg-[#112240] text-cyan-400 px-3 py-1 rounded-lg text-sm">
        {Math.round(scale * 100)}%
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center"
        onWheel={handleWheel}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className={`max-w-full max-h-full object-contain select-none ${
            scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
          }`}
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? "none" : "transform 0.2s ease-out",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-[#112240] text-cyan-400 px-4 py-2 rounded-lg text-sm">
        Scroll to zoom • Drag to pan • X to close
      </div>
    </div>
  );
};

export default ImageViewer;